import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { Alert, Field, InlineRow, Muted, Stack, Strong, Textarea } from '../../components/ui';
import {
  useAppealWorkerStrikeMutation,
  useGetPresignedUrlForServiceDocumentMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { documentFileProblem, uploadServiceDocument } from './serviceDocuments';

const HiddenFileInput = styled.input`
  ${tw`hidden`}
`;

/** The Provider appeals a late cancellation or no-show (spec 019 D17); a brand admin decides. */
export function StrikeAppealModal({
  eventId,
  label,
  onClose,
}: {
  eventId: string;
  label: string;
  onClose: () => void;
}) {
  const [appeal, { loading }] = useAppealWorkerStrikeMutation({
    refetchQueries: ['JobTimeline', 'WorkerStrikes'],
  });
  const [presign] = useGetPresignedUrlForServiceDocumentMutation();
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (!note.trim()) {
      setError('Say why the strike is wrong');
      return;
    }
    setError(null);
    try {
      let fileKey: string | null = null;
      if (file) {
        const problem = documentFileProblem(file);
        if (problem) throw new Error(problem);
        fileKey = await uploadServiceDocument(file, async (contentType, contentLength) => {
          const { data } = await presign({ variables: { contentType, contentLength } });
          if (!data?.getPresignedUrlForServiceDocument)
            throw new Error('Could not start the upload');
          return data.getPresignedUrlForServiceDocument;
        });
      }
      await appeal({ variables: { eventId, note: note.trim(), fileKey } });
      toast.success('Appeal sent. We review it and tell you.');
      onClose();
    } catch (e) {
      setError(stayErrorMessage(e, 'Could not send the appeal'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Appeal a strike">
      <Stack $gap={4}>
        <Strong>Appeal: {label}</Strong>
        <Muted $size="sm">
          Tell us what happened. A brand admin reviews it; a removed strike no longer counts.
        </Muted>
        <Field label="Why is it wrong?" htmlFor="appeal-note" required>
          <Textarea
            id="appeal-note"
            rows={3}
            maxLength={2000}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </Field>
        <HiddenFileInput
          ref={input}
          type="file"
          accept="application/pdf,image/*"
          onChange={e => setFile(e.target.files?.[0] ?? null)}
        />
        <InlineRow>
          <Button
            type="button"
            theme="secondary"
            size="sm"
            text="Attach a file"
            onClick={() => input.current?.click()}
          />
          <Muted $size="sm">{file ? file.name : 'Optional: a screenshot or a receipt'}</Muted>
        </InlineRow>
        {error && (
          <Alert tone="error" title="Not sent">
            {error}
          </Alert>
        )}
        <InlineRow>
          <Button
            type="button"
            text="Send appeal"
            onClick={submit}
            isLoading={loading}
            dataTestId="strike-appeal-send"
          />
          <Button type="button" theme="text" text="Cancel" onClick={onClose} />
        </InlineRow>
      </Stack>
    </Modal>
  );
}
